import React, { useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import data from '../data/products.json';
import './Products.css';

export default function Products(){
  const params = useParams();
  const navigate = useNavigate();
  const { categoryId, subId } = params;
  const [q, setQ] = useState('');
  // compute category/subcategory early so hooks can be used unconditionally
  const category = data.categories.find((c) => c.id === categoryId);
  const subcategories = category?.subcategories || [];
  const subcategory = subcategories.find?.((s) => s.id === subId);
  const directProducts = category?.products || [];

  const filtered = useMemo(() => {
    const query = (q || '').toLowerCase();

    const filterByName = (p) => {
      const name = (p && p.name ? p.name : '').toLowerCase();
      return name.includes(query);
    };

    if (subcategory) return (subcategory.products || []).filter(filterByName);
    return (directProducts || []).filter(filterByName);
  }, [subcategory, directProducts, q]);

  // No category selected: show base category cards
  if(!categoryId){
    return (
      <main className="products-page container">
        <h2>Product Categories</h2>
        <div className="cards-grid">
          {data.categories.map((c) => {
            // pick an image from first product if present
            const img = c.subcategories?.[0]?.products?.[0]?.image || c.products?.[0]?.image || `https://via.placeholder.com/600x360?text=${encodeURIComponent(c.name)}`;
            return (
              <div key={c.id} className="card category-card" onClick={() => navigate(`/products/${c.id}`)}>
                <img src={img} alt={c.name} />
                <div className="card-body">
                  <h3>{c.name}</h3>
                  <p>{(c.subcategories || []).length} subcategories</p>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    );
  }

  if(!category){
    return <main style={{padding:24}}><h2>Category not found</h2></main>;
  }

  // Category selected, but no subcategory: show subcategory cards
  if(categoryId && !subId && subcategories.length > 0){
    return (
      <main className="products-page container">
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <h2>{category.name} — Subcategories</h2>
          <div><Link to="/products">Back to categories</Link></div>
        </div>

        <div className="cards-grid">
          {subcategories.map((s) => {
            const img = s.products?.[0]?.image || `https://via.placeholder.com/600x360?text=${encodeURIComponent(s.name)}`;
            return (
              <div key={s.id} className="card subcategory-card" onClick={() => navigate(`/products/${categoryId}/${s.id}`)}>
                <img src={img} alt={s.name} />
                <div className="card-body">
                  <h3>{s.name}</h3>
                  <p>{s.products.length} products</p>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    );
  }

  // Category with direct products (no subcategories)
  if(categoryId && !subId && subcategories.length === 0){
    return (
      <main className="products-page container">
        <div className="product-header" style={{marginBottom:12}}>
          <h2>{category.name}</h2>
          <div>
            <input placeholder="Search products..." value={q} onChange={(e)=>setQ(e.target.value)} className="search" />
          </div>
        </div>

        <div className="cards-grid products-grid">
          {filtered.length===0 && <div>No products match your search.</div>}
          {filtered.map(p => (
            <Link key={p.id} to={`/product/${p.id}`} className="card product-card modern-card modern-card-link">
              {p.image && <img src={p.image} alt={p.name} className="modern-card-img" />}
              <div className="card-body modern-card-body">
                <h4 className="modern-card-title accent-bg">{p.name}</h4>
                {typeof p.specs === 'string' && p.specs.length > 0 && (
                  <div className="modern-card-specs-preview">{p.specs.length > 60 ? p.specs.slice(0, 60) + '...' : p.specs}</div>
                )}
              </div>
            </Link>
          ))}
        </div>
      </main>
    );
  }

  // Both category and subId present: show products list with right-side subcategory list
  if(!subcategory){
    return <main style={{padding:24}}><h2>Subcategory not found</h2></main>;
  }

  return (
    <main className="products-page container">
      <div className="products-layout">
        <div className="main-col">
          <div className='product-header'>
            <h2>{category.name} / {subcategory.name}</h2>
            <div>
              <input placeholder="Search products..." value={q} onChange={(e)=>setQ(e.target.value)} className="search" />
            </div>
          </div>

          <div className="cards-grid products-grid">
            {filtered.length===0 && <div>No products match your search.</div>}
            {filtered.map(p => (
              <Link key={p.id} to={`/product/${p.id}`} className="card product-card modern-card modern-card-link">
                {p.image && <img src={p.image} alt={p.name} className="modern-card-img" />}
                <div className="card-body modern-card-body">
                  <h4 className="modern-card-title accent-bg">{p.name}</h4>
                  {/* Optionally show short description or specs preview here */}
                  {typeof p.specs === 'string' && p.specs.length > 0 && (
                    <div className="modern-card-specs-preview">{p.specs.length > 60 ? p.specs.slice(0, 60) + '...' : p.specs}</div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>

        <aside className="side-col">
          <h4>Subcategories</h4>
          <ul className="vertical-list">
            {category.subcategories.map((s) => (
              <li key={s.id} className={s.id===subId? 'active':''} onClick={() => navigate(`/products/${categoryId}/${s.id}`)}>
                {s.name} <span className="count">{s.products.length}</span>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </main>
  );
}
